import React, { useState } from 'react';
import AdminCRUD from '../../components/admin/AdminCRUD';
import I18nForm from '../../components/admin/I18nForm';

const CATEGORIES = ['market-intelligence', 'digital-commerce', 'digital-marketing', 'technology', 'operations', 'general'];
const PAGES = ['/', '/services', '/insights', '/about', '/case-studies'];

const PressRefForm = ({ initialData, onSave, onCancel }) => {
  const d = initialData || {};
  const [form, setForm] = useState({
    publication: d.publication || { name: '', website: '', logoKey: '' },
    articleTitle: d.articleTitle || { en: '', 'zh-CN': '', 'zh-TW': '', ja: '' },
    articleUrl:   d.articleUrl   || '',
    summary:      d.summary      || { en: '', 'zh-CN': '', 'zh-TW': '', ja: '' },
    publishedDate: d.publishedDate || '',
    category:     d.category     || 'general',
    relevantPages: d.relevantPages || [],
    isFeatured:   d.isFeatured   || false,
    isActive:     d.isActive !== false,
    order:        d.order        || 0,
  });

  const inputStyle = { width: '100%', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px', fontSize: '13px', color: '#334155', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' };

  const togglePage = (page) => {
    setForm(f => ({
      ...f,
      relevantPages: f.relevantPages.includes(page)
        ? f.relevantPages.filter(p => p !== page)
        : [...f.relevantPages, page]
    }));
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
        <div>
          <label style={labelStyle}>Publication Name</label>
          <input value={form.publication.name} onChange={e => setForm(f => ({ ...f, publication: { ...f.publication, name: e.target.value } }))} required style={inputStyle} placeholder="Harvard Business Review" />
        </div>
        <div>
          <label style={labelStyle}>Publication Website</label>
          <input value={form.publication.website} onChange={e => setForm(f => ({ ...f, publication: { ...f.publication, website: e.target.value } }))} style={inputStyle} placeholder="https://hbr.org" />
        </div>
        <div>
          <label style={labelStyle}>Logo Key</label>
          <input value={form.publication.logoKey} onChange={e => setForm(f => ({ ...f, publication: { ...f.publication, logoKey: e.target.value } }))} style={inputStyle} placeholder="hbr" />
        </div>
      </div>

      <I18nForm label="Article Title" value={form.articleTitle} onChange={v => setForm(f => ({ ...f, articleTitle: v }))} />
      <I18nForm label="Summary" value={form.summary} onChange={v => setForm(f => ({ ...f, summary: v }))} multiline rows={3} />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
        <div>
          <label style={labelStyle}>Article URL</label>
          <input value={form.articleUrl} onChange={e => setForm(f => ({ ...f, articleUrl: e.target.value }))} required style={inputStyle} placeholder="https://..." />
        </div>
        <div>
          <label style={labelStyle}>Published Date</label>
          <input value={form.publishedDate} onChange={e => setForm(f => ({ ...f, publishedDate: e.target.value }))} style={inputStyle} placeholder="February 26, 2025" />
        </div>
        <div>
          <label style={labelStyle}>Category</label>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={{ ...inputStyle, background: '#fff' }}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>Relevant Pages</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {PAGES.map(p => (
            <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer', background: form.relevantPages.includes(p) ? '#f0f9ff' : '#f8fafc', padding: '4px 10px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
              <input type="checkbox" checked={form.relevantPages.includes(p)} onChange={() => togglePage(p)} />
              {p}
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>Order</label>
          <input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} style={inputStyle} />
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', fontSize: '13px', cursor: 'pointer', paddingBottom: '2px' }}>
          <input type="checkbox" checked={form.isFeatured} onChange={e => setForm(f => ({ ...f, isFeatured: e.target.checked }))} />
          Featured (show on homepage)
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', fontSize: '13px', cursor: 'pointer', paddingBottom: '2px' }}>
          <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
          Active
        </label>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="submit" style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Save</button>
        <button type="button" onClick={onCancel} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', padding: '9px 20px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
      </div>
    </form>
  );
};

const COLUMNS = [
  { key: 'publication', label: 'Publication', render: item => item.publication?.name || '' },
  { key: 'articleTitle', label: 'Title (EN)', render: item => item.articleTitle?.en || '' },
  { key: 'category', label: 'Category' },
  { key: 'publishedDate', label: 'Published' },
  { key: 'isFeatured', label: 'Featured', render: item => item.isFeatured ? '⭐' : '' },
  { key: 'isActive', label: 'Active', render: item => item.isActive ? '✅' : '❌' },
];

const AdminPressReferences = () => (
  <AdminCRUD title="Press References" apiPath="/api/admin/press-references" columns={COLUMNS} FormComponent={PressRefForm} />
);

export default AdminPressReferences;
